/**
 * The wild pet (Pets v2, docs/PETS.md; Kevin 2026-10-14: "have the pets appear once per level, randomly, running around
 * … if you tap the pet then it comes onto the road as a boss to fight, if you beat it then you unlock the pet, or if you
 * already have that pet, it levels up … when the pet spawns it has a very low % chance of wearing the skin").
 *
 * - Only on a run whose modifiers carry `wildPet` (the app adds it to campaign runs: first clears, replays, Heroic and
 *   Trial runs) from `pets.wild.fromLevel`; never Iron, Endless, Daily Road, challenge codes or event modes (refused
 *   here too). At most once a run.
 * - The encounter is drawn from the run's seed and level (tag "wildPet", never the run's RNG, so a run without one
 *   plays — and hashes — exactly as before, and a checkpoint retry or a replay meets the very same pet): its stage,
 *   uniform in [⌈stages × from %⌉, ⌊stages × to %⌋] and never the boss stage; then its skin: one of the rule's
 *   `skins` (the pet's skins the player lacks, or all once every one is owned) with `skinChanceBp` basis points.
 * - `appearDelayTicks` of running action after its stage starts it appears on the grass (WildPetAppeared) for
 *   `visibleTicks` of running action (an open card offer or the Wyrm's choice pauses that clock); untapped, it runs
 *   off (WildPetLeft).
 * - `Commands.engagePet()` while it is visible (never while the action is paused): it comes out of the portal now as a
 *   boss (WildPetEngaged; a boss for targeting, control and area damage, no boss mechanic, no BossSpawned) with the
 *   level's boss HP at its stage × its chapter's `hpMult`, at its archetype's speed; a troop holding it takes `biteFraction` of its
 *   max HP a strike. Tapped between waves, the next stage starts at once and the pet comes with it (the enemy systems
 *   run only while a wave does), with no early-call points. While it is on the road it is a raider like any other: no
 *   stage clears and the level is not won until it is caught or gone.
 * - It never hurts the fortress: at the gate it escapes (WildPetEscaped): no leak (the "no leaks" star and the chest
 *   band never see it), no damage. Killed: caught (WildPetCaught), `catchPoints` kill points. What a catch pays the
 *   player (a pet, a level, gold, the skin) is the app's, once per run (meta/petCatch.ts, app pets.ts).
 */
import { BalanceData, bossHpMultOf, stagePowerOf } from "../data/balance";
import { BASIS_POINTS, firstCatchHpAt, wildHpMultAt } from "../data/pets";
import { Fix64 } from "../maths/fix64";
import { Fnv1a64 } from "../maths/fnv1a64";
import { Pcg32 } from "../maths/pcg32";
import { CommandResult } from "./command";
import { Enemy } from "./entities";
import { SimEventKind } from "./events";
import { LevelPhase, type LevelSim } from "./levelSim";
import type { WildPetRule } from "./petRule";
import { spawn } from "./systems/enemyMotion";

export type WildPetPhase = "pending" | "waiting" | "visible" | "left" | "engaged" | "caught" | "escaped";
export const WILD_PET_PHASES: readonly WildPetPhase[] = ["pending", "waiting", "visible", "left", "engaged", "caught", "escaped"];

/** The run's wild pet (LevelSim.wildPet; null on a run without one). */
export interface WildPetRun {
  readonly petId: string;
  /** Its enemy archetype on the road (the enemies.json enemy whose `wildPet` is the pet). */
  readonly archetype: number;
  /** The stage it appears on (drawn from the seed and level). */
  readonly stage: number;
  /** The skin it wears: an index into the rule's `skins`, −1 for none. */
  readonly skin: number;
  phase: WildPetPhase;
  /** Waiting: running ticks until it appears; visible: running ticks until it runs off; 0 otherwise. */
  ticksLeft: number;
  /** Its enemy's id once engaged (0 before). */
  enemyId: number;
}

const WILD_TAG = "wildPet";

/** The pure draw of a run's encounter: its stage (0: the level has no stage in the window) and skin (an index, −1 none). */
export function drawWildPet(balance: BalanceData, levelNumber: number, seed: bigint, stages: number, rule: WildPetRule): { readonly stage: number; readonly skin: number } {
  const W = balance.pets.wild;
  const lo = Math.max(1, Math.ceil((stages * W.stageFromPercent) / 100)), hi = Math.min(stages - 1, Math.floor((stages * W.stageToPercent) / 100));
  const h = new Fnv1a64();
  h.writeString(WILD_TAG); h.writeLong(seed); h.writeInt(levelNumber);
  const rng = new Pcg32(h.value);
  if (hi < lo) return { stage: 0, skin: -1 };
  const stage = lo + rng.nextInt(hi - lo + 1);
  const skinned = rng.nextInt(BASIS_POINTS) < rule.skinChanceBp;
  return { stage, skin: skinned && rule.skins.length > 0 ? rng.nextInt(rule.skins.length) : -1 };
}

/** The archetype of pet `petId`'s wild encounter, or −1. */
export function wildPetArchetype(balance: BalanceData, petId: string): number { return balance.enemies.find((e) => e.wildPet === petId)?.index ?? -1; }

/** The run's encounter when the sim is built: null unless its modifiers carry one and the mode and level allow it. */
export function wildPetEncounter(sim: LevelSim): WildPetRun | null {
  const rule = sim.mods.wildPet, m = sim.mods;
  if (rule === null || m.iron !== null || m.endless !== null || m.daily !== null || m.event !== null || m.challenge !== null) return null;
  if (sim.levelNumber < sim.balance.pets.wild.fromLevel) return null;
  const archetype = wildPetArchetype(sim.balance, rule.id);
  if (archetype < 0) return null;
  const { stage, skin } = drawWildPet(sim.balance, sim.levelNumber, sim.seed, sim.stages, rule);
  return stage > 0 ? { petId: rule.id, archetype, stage, skin, phase: "pending", ticksLeft: 0, enemyId: 0 } : null;
}

/** The skin the run's wild pet wears (a catalogue id), or null. */
export function wildPetSkin(sim: LevelSim): string | null {
  const w = sim.wildPet;
  return w === null || w.skin < 0 ? null : sim.mods.wildPet?.skins[w.skin] ?? null;
}

/** A stage began (fresh or joined to a running wave): the wild pet's stage starts its appearance clock. */
export function wildPetAtStage(sim: LevelSim, stage: number): void {
  const w = sim.wildPet;
  if (w === null || w.phase !== "pending" || stage !== w.stage || sim.debugSandbox) return;
  w.phase = "waiting";
  w.ticksLeft = sim.balance.pets.wild.appearDelayTicks;
}

/** Each running (unpaused) tick of a wave or a break: it appears when its delay runs out, and runs off when its time does. */
export function tickWildPet(sim: LevelSim): void {
  const w = sim.wildPet;
  if (w === null || sim.isOver || (w.phase !== "waiting" && w.phase !== "visible")) return;
  if (w.ticksLeft > 0) w.ticksLeft--;
  if (w.ticksLeft > 0) return;
  if (w.phase === "waiting") {
    w.phase = "visible";
    w.ticksLeft = sim.balance.pets.wild.visibleTicks;
    sim.events.add(SimEventKind.WildPetAppeared, w.skin + 1, w.ticksLeft, w.stage);
  } else {
    w.phase = "left";
    sim.events.add(SimEventKind.WildPetLeft, w.skin + 1, 0, w.stage);
  }
}

/** Whether `Commands.engagePet()` would be taken now (the HUD's tap target). */
export function canEngagePet(sim: LevelSim): boolean {
  return sim.wildPet !== null && sim.wildPet.phase === "visible" && !sim.isOver && !sim.pausedForOffer && (sim.phase === LevelPhase.WaveRunning || sim.phase === LevelPhase.BetweenWaves);
}

/**
 * The wild pet's HP multiplier on its archetype: the level's boss at the encounter's stage (levelBase × stagePower ×
 * Heroic's enemyHpMult × bossHpMult × the boss archetype's HP) × `pets.wild.hpMult` of the level's chapter, × the pet's
 * own `wildHpScale` (Pets v3); a first catch's, the level's base power × firstCatchHp × the pet's `firstCatchScale`.
 */
export function wildPetHpMult(sim: LevelSim, stage: number, archetype: number): Fix64 {
  const B = sim.balance;
  let power = B.tables.levelBaseAt(sim.levelNumber).mul(stagePowerOf(B, sim.level, stage));
  if (!sim.mods.enemyHpMult.eq(Fix64.One)) power = power.mul(sim.mods.enemyHpMult);
  // a first catch (no pup of the player's own helps): the level's base power × firstCatchHp, never the level's boss
  const first = sim.mods.wildPet?.firstCatch === true ? firstCatchHpAt(B.pets, sim.levelNumber) : null;
  // Pets v3: each pet's chase has its own health scales, owned and first (One for the pup: its numbers untouched)
  const pet = B.pets.list.find((p) => p.id === B.enemies[archetype].wildPet);
  const scale = pet === undefined ? Fix64.One : first !== null ? pet.firstCatchScale : pet.wildHpScale;
  if (!scale.eq(Fix64.One)) power = power.mul(scale);
  if (first !== null) return power.mul(Fix64.fromInt(first)).div(Fix64.max(B.enemies[archetype].hp, Fix64.One));
  const boss = power.mul(bossHpMultOf(B, sim.level)).mul(B.enemies[sim.level.bossEnemyIndex].hp);
  return boss.mul(wildHpMultAt(B.pets, sim.levelNumber)).div(Fix64.max(B.enemies[archetype].hp, Fix64.One));
}

/** `Commands.engagePet()`: it comes out of the portal now (between waves the next stage starts with it). */
export function engagePet(sim: LevelSim): CommandResult {
  const w = sim.wildPet;
  if (w === null || !canEngagePet(sim)) return CommandResult.NoWildPet;
  if (sim.phase === LevelPhase.BetweenWaves) sim.startStage(sim.stage + 1);
  spawn(sim, { tick: sim.tick, archetype: w.archetype, hpMult: wildPetHpMult(sim, w.stage, w.archetype), isBoss: true, pointsOverride: sim.balance.pets.wild.catchPoints, wildPet: true });
  return CommandResult.Ok;
}

/** The pet came out of the portal (enemyMotion.spawn): engaged, harmless to the fortress, its bite on troops set. */
export function onWildPetSpawned(sim: LevelSim, e: Enemy): void {
  const w = sim.wildPet!;
  w.phase = "engaged";
  w.ticksLeft = 0;
  w.enemyId = e.id;
  e.leakDamage = 0;
  e.hitFraction = sim.balance.pets.wild.biteFraction;
  sim.events.add(SimEventKind.WildPetEngaged, e.id, w.skin + 1, e.archetype);
}

/** Whether `e` is the run's wild pet on the road. */
export function isLiveWildPet(sim: LevelSim, e: Enemy): boolean { return sim.wildPet !== null && sim.wildPet.phase === "engaged" && sim.wildPet.enemyId === e.id; }

/** It was beaten (applyDamage): caught. */
export function onWildPetCaught(sim: LevelSim, e: Enemy): void {
  sim.wildPet!.phase = "caught";
  sim.events.add(SimEventKind.WildPetCaught, e.id, sim.wildPet!.skin + 1, e.archetype);
}

/** It reached the gate (moveEnemies, in place of a leak): it got away, doing no damage. */
export function onWildPetEscaped(sim: LevelSim, e: Enemy): void {
  sim.wildPet!.phase = "escaped";
  sim.events.add(SimEventKind.WildPetEscaped, e.id, sim.wildPet!.skin + 1, e.archetype);
}

const WILD_RUN_HASH_TAG = 0x3d1e;
/** The encounter's state in the state hash (only on a run that has one). */
export function writeWildPetHash(sim: LevelSim, h: Fnv1a64): void {
  const w = sim.wildPet;
  if (w === null) return;
  h.writeInt(WILD_RUN_HASH_TAG); h.writeInt(w.archetype); h.writeInt(w.stage); h.writeInt(w.skin); h.writeInt(WILD_PET_PHASES.indexOf(w.phase)); h.writeInt(w.ticksLeft); h.writeInt(w.enemyId);
}
